import React, { useMemo, useState } from "react";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";
import { CheckCircle2, Circle } from "lucide-react";

const ITEMS = [
  ["resume",        "Resume complete"],
  ["license",       "License(s) active"],
  ["certs",         "Certifications present"],
  ["skills",        "Skills checklist complete"],
  ["refs",          "References available (2+)"],
  ["authz",         "Work authorization confirmed"],
  ["pay",           "Pay package confirmed"],
  ["availability",  "Start availability confirmed"],
];

export default function SubmittalScore() {
  React.useEffect(() => {
    document.title = "TRITAL Submittal Score™ — MSP/VMS Submission Readiness | Workforce OS™";
  }, []);
  const [state, setState] = useState(() => Object.fromEntries(ITEMS.map(([k]) => [k, false])));
  const score = useMemo(() => Math.round((Object.values(state).filter(Boolean).length / ITEMS.length) * 100), [state]);
  const missing = ITEMS.filter(([k]) => !state[k]).map(([_, l]) => l);

  const status =
    score >= 90 ? { label: "Ready to submit", card: "bg-emerald-50 border-emerald-200", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" } :
    score >= 70 ? { label: "Almost there",    card: "bg-amber-50 border-amber-200",     pill: "bg-amber-100 text-amber-800 border-amber-200" } :
    { label: "Not ready", card: "bg-rose-50 border-rose-200", pill: "bg-rose-100 text-rose-800 border-rose-200" };

  return (
    <WorkforceOsLayout
      breadcrumb="Submittal Score"
      title="TRITAL Submittal Score™"
      description="An 8-point submission readiness check before any clinician package goes to an MSP/VMS. Free, instant, and recruiter-ready."
    >
      <section className="py-10">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="submittal-checklist">
            <h3 className="font-display font-bold text-lg text-trital-navy">Submission Readiness Checklist</h3>
            <ul className="mt-4 divide-y divide-slate-100">
              {ITEMS.map(([key, label]) => {
                const on = state[key];
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => setState({ ...state, [key]: !on })}
                      className="w-full flex items-center gap-3 py-3 text-left"
                      data-testid={`submittal-item-${key}`}
                      aria-pressed={on}
                    >
                      {on ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.8} />
                      ) : (
                        <Circle className="w-5 h-5 text-slate-400 shrink-0" strokeWidth={1.8} />
                      )}
                      <span className={`text-[14px] ${on ? "text-slate-900 font-semibold" : "text-slate-600"}`}>{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <aside className="space-y-4">
            <div className={`rounded-2xl border p-6 ${status.card}`} data-testid="submittal-score-card">
              <div className="overline text-slate-500">Submittal Score</div>
              <div className="mt-1 flex items-end gap-3">
                <div className="font-display font-black text-6xl tabular-nums text-trital-navy" data-testid="submittal-score">
                  {score}
                </div>
                <div className="pb-2 text-trital-navy/60 font-semibold">/ 100</div>
              </div>
              <div className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold border ${status.pill}`} data-testid="submittal-status">
                {status.label}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h4 className="font-display font-bold text-trital-navy">Recruiter action list</h4>
              {missing.length === 0 ? (
                <p className="mt-3 text-[13.5px] text-emerald-700">All items complete. Ready to submit to MSP/VMS.</p>
              ) : (
                <ul className="mt-3 text-[13.5px] text-slate-700 space-y-1.5 list-disc pl-5" data-testid="submittal-actions">
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </section>
      <RebecaMiguelChatbot variant="floating" />
    </WorkforceOsLayout>
  );
}
